import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import { split, merge, reduce, values } from 'lodash';
import React from 'react';

import {
  PrimaryHeading,
  SecondaryHeading,
  TertiaryHeading,
  QuaternaryHeading,
  QuinaryHeading,
} from 'components/Headings';
import HorizontalRule from 'components/HorizontalRule';
import InlineLink from 'components/InlineLink';
import { UnorderedList, ListItem } from 'components/List';
import Paragraph from 'components/Paragraph';
import Strong from 'components/Strong';
import Log from 'utils/Log';

const RichTextProcessor = class {
  constructor(richTextObject, additionalOptions = {}) {
    this.richTextObject = richTextObject;
    this.options = merge({}, RichTextProcessor.defaultOptions, additionalOptions);
  }

  static getComponent = ({ children, node, type }) => {
    switch (type) {
      case BLOCKS.HEADING_1:
        return <PrimaryHeading>{children}</PrimaryHeading>;
      case BLOCKS.HEADING_2:
        return <SecondaryHeading>{children}</SecondaryHeading>;
      case BLOCKS.HEADING_3:
        return <TertiaryHeading>{children}</TertiaryHeading>;
      case BLOCKS.HEADING_4:
        return <QuaternaryHeading>{children}</QuaternaryHeading>;
      case BLOCKS.HEADING_5:
        return <QuinaryHeading>{children}</QuinaryHeading>;
      case BLOCKS.LIST_ITEM:
        return <ListItem>{children}</ListItem>;
      case BLOCKS.PARAGRAPH: {
        // filter out empty <p> tags (https://github.com/contentful/rich-text/issues/101)
        const isEmptyChildren = children?.toString().trim() === '';
        if (isEmptyChildren) return null;
        return <Paragraph>{children}</Paragraph>;
      }
      case BLOCKS.UL_LIST:
        return <UnorderedList>{children}</UnorderedList>;
      case INLINES.HYPERLINK:
        return <InlineLink linkTo={node.data.uri}>{children}</InlineLink>;
      case BLOCKS.HR:
        return <HorizontalRule />;
      case BLOCKS.DOCUMENT:
        return <>{children}</>;
      case MARKS.BOLD:
        return <Strong>{children}</Strong>;
      case BLOCKS.EMBEDDED_ENTRY:
        return <>{children}</>;
      case INLINES.EMBEDDED_ENTRY:
        return <>{children}</>;
      case BLOCKS.EMBEDDED_ASSET:
      case BLOCKS.OL_LIST:
      case BLOCKS.HEADING_6:
      case BLOCKS.QUOTE:
      case INLINES.ASSET_HYPERLINK:
      case INLINES.ENTRY_HYPERLINK:
      case MARKS.ITALIC:
      case MARKS.UNDERLINE:
      case MARKS.CODE:
      default:
        return null;
    }
  };

  static get renderNodeHandlers() {
    return reduce(
      [...values(BLOCKS), ...values(INLINES)],
      (accumulator, type) => {
        accumulator[type] = (node, children) =>
          RichTextProcessor.getComponent({ children, node, type });

        return accumulator;
      },
      {}
    );
  }

  static get renderMarkHandlers() {
    return reduce(
      MARKS,
      (accumulator, type) => {
        accumulator[type] = text =>
          RichTextProcessor.getComponent({ children: text, node: null, type });

        return accumulator;
      },
      {}
    );
  }

  static renderTextHandler(text) {
    return reduce(
      split(text, '\n'),
      (accumulator, textSegment, index) => {
        return [
          ...accumulator,
          index > 0 && <br key={`rich-text-line-break-${index}`} />,
          textSegment,
        ];
      },
      []
    );
  }

  static get defaultOptions() {
    return {
      renderNode: RichTextProcessor.renderNodeHandlers,
      renderMark: RichTextProcessor.renderMarkHandlers,
      renderText: RichTextProcessor.renderTextHandler,
    };
  }

  getReactComponents() {
    try {
      return documentToReactComponents(this.richTextObject, this.options);
    } catch (e) {
      Log.error(e, 'utils:RichTextProcessor.getReactComponents failed!');
      return null;
    }
  }
};

export default RichTextProcessor;
